import { useEffect, useState } from "react";
import { MapPin, Copy, Calendar, ExternalLink, Home as HomeIcon,
         User as UserIcon, Signal, Voicemail, Phone as PhoneIcon,
         X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Exact Batch-Dialer top strip + tabs. Full-width, sits above the
// LEAD INFO / PROPERTY / ACTIVITIES content grid.

const TABS = [
  { key: "lead_info",  label: "Lead Info" },
  { key: "property",   label: "Property Details" },
  { key: "activities", label: "Activities" },
  { key: "campaigns",  label: "Campaigns" },
  { key: "notes",      label: "Notes" },
  { key: "sheets",     label: "Lead Sheets" },
];

function useLiveTimer(startedAt) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return "00:00:00";
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function copyText(txt, label) {
  if (!txt) return;
  try { navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
  catch { toast.error("Copy failed"); }
}

// Default the callback picker to ~1 hour from now, rounded to 5 min.
function defaultCallbackIso() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(Math.round(d.getMinutes() / 5) * 5, 0, 0);
  // Convert to local-timezone <input type=datetime-local> value: YYYY-MM-DDTHH:MM
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DialerBatchHeader({
  lead, callState, callStartedAt,
  activeTab, onTabChange,
  dispoCount = 0, callCount = 0, vmCount = 0,
  onCallbackSaved,
}) {
  const timer = useLiveTimer(callStartedAt);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [when, setWhen] = useState(defaultCallbackIso());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (scheduleOpen) {
      setWhen(defaultCallbackIso());
      setNote("");
    }
  }, [scheduleOpen]);

  if (!lead) return null;

  const addr = lead.property_address || "";
  const email = lead.email || "";
  const gmapsHref = addr
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`
    : null;
  const zillowHref = addr
    ? `https://www.zillow.com/homes/${encodeURIComponent(addr.replace(/\s+/g, "-"))}_rb/`
    : null;

  const stateLine = (() => {
    if (callState === "connected")  return "On Call";
    if (callState === "ringing" || callState === "connecting") return "Outbound Call";
    if (callState === "ended")      return "Call ended";
    return "Idle";
  })();

  const saveCallback = async () => {
    if (!when) return toast.error("Pick a date and time");
    setBusy(true);
    try {
      // Convert local datetime-local string to ISO UTC.
      const iso = new Date(when).toISOString();
      const { data } = await api.patch(`/leads/${lead.id}`, {
        callback_at: iso,
        callback_note: note || undefined,
      });
      toast.success(`Callback scheduled for ${new Date(iso).toLocaleString()}`);
      setScheduleOpen(false);
      onCallbackSaved && onCallbackSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule callback");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm"
      data-testid="dialer-batch-header"
    >
      {/* ═══ TOP STRIP ═══ */}
      <div className="flex items-center gap-3 px-3 py-2 bg-slate-100/70 border-b border-slate-200">
        {/* Contact avatar block */}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-200 text-slate-700"
               data-testid="dbh-contact-block">
            <UserIcon size={14} />
            <span className="text-[11px] font-semibold uppercase tracking-widest truncate max-w-[100px]">
              {(lead.name || "").split(" ")[0] || "Lead"}
            </span>
          </div>
          <CounterPill icon={<span className="w-3 h-3 rounded-sm bg-blue-500" />} value={dispoCount} testid="dbh-dispo-count" />
          <CounterPill icon={<PhoneIcon size={12} className="text-slate-600" />} value={callCount} testid="dbh-call-count" />
          <CounterPill icon={<Voicemail size={12} className="text-slate-600" />} value={vmCount} testid="dbh-vm-count" />
        </div>

        {/* Address · Phone · Email cluster */}
        <div className="flex-1 grid grid-cols-3 gap-4 min-w-0">
          <LabeledField label="Address" value={addr} testid="dbh-address"
                        onCopy={() => copyText(addr, "Address")} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              Phone
            </div>
            <div className="text-[11px] text-slate-600 truncate" data-testid="dbh-phone">
              {stateLine}
              {callStartedAt && (callState === "connected" || callState === "ringing") ? (
                <span className="font-mono ml-1.5 text-slate-500">{timer}</span>
              ) : null}
            </div>
          </div>
          <LabeledField label="Email" value={email} testid="dbh-email"
                        onCopy={() => copyText(email, "Email")} />
        </div>

        {/* Right cluster: calendar + Maps + Zillow + signal */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            className="w-8 h-8 rounded-md bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition"
            title="Schedule callback"
            onClick={() => setScheduleOpen(true)}
            data-testid="dbh-calendar-btn"
          >
            <Calendar size={14} />
          </button>
          {gmapsHref && (
            <a href={gmapsHref} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded border border-emerald-500 bg-white hover:bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-1 transition"
               data-testid="dialer-header-gmaps-btn"
               title="Open in Google Maps">
              <MapPin size={11} /> Google Maps
            </a>
          )}
          {zillowHref && (
            <a href={zillowHref} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded border border-blue-500 bg-white hover:bg-blue-50 text-blue-700 text-[11px] font-medium px-2 py-1 transition"
               data-testid="dialer-header-zillow-btn"
               title="Open in Zillow">
              <ExternalLink size={11} /> Zillow
            </a>
          )}
          {/* Quick offer button removed 2026-08-28 — 3-tier offer ladder
              already lives under the phones column in the Lead Info tab. */}
          <Signal size={14} className={callState === "connected" ? "text-emerald-500" : "text-slate-300"} />
        </div>
      </div>

      {/* Status pill (kept for tests/telemetry, discreet) */}
      <div className="hidden">
        <span data-testid="dialer-status-pill">{stateLine} {timer}</span>
      </div>

      {/* ═══ TABS ═══ */}
      {onTabChange && (
        <div className="flex items-center gap-0.5 px-2 border-b border-slate-200 bg-white overflow-x-auto"
             data-testid="dialer-batch-tabs">
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => onTabChange(t.key)}
                data-testid={`dialer-tab-${t.key}`}
                className={`text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 border-b-2 whitespace-nowrap transition ${
                  active
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ═══ Callback scheduler dialog (owner ask 2026-08-28) ═══
          Rep taps the blue calendar → picks a date/time → PATCH lead
          with callback_at, which the queue re-surfaces after the time
          hits (existing callback engine already looks at this field). */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md" data-testid="dbh-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar size={16} className="text-blue-600" /> Schedule callback
            </DialogTitle>
            <DialogDescription>
              We'll re-surface <b>{lead.name || "this lead"}</b> in your
              queue at the time you pick. Optionally jot a note so future-you
              remembers what to say.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                When to call back
              </Label>
              <Input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="mt-1 h-10 text-sm font-mono"
                data-testid="dbh-schedule-when"
              />
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {[
                  { label: "In 1 hour", ms: 60 * 60 * 1000 },
                  { label: "Tomorrow 10am", fn: () => {
                    const d = new Date(); d.setDate(d.getDate() + 1);
                    d.setHours(10, 0, 0, 0); return d;
                  }},
                  { label: "In 3 days", ms: 3 * 24 * 60 * 60 * 1000 },
                  { label: "Next week", ms: 7 * 24 * 60 * 60 * 1000 },
                ].map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      const d = s.fn ? s.fn() : new Date(Date.now() + s.ms);
                      const pad = (n) => String(n).padStart(2, "0");
                      setWhen(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                    }}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-600"
                    data-testid={`dbh-quick-${s.label.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Note (optional)
              </Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Wife needs to be on the line"
                className="mt-1 h-9 text-sm"
                data-testid="dbh-schedule-note"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={busy}
                    className="rounded-md h-9 px-4 border-slate-300 text-slate-600"
                    data-testid="dbh-schedule-cancel">
              <X size={13} className="mr-1.5" /> Cancel
            </Button>
            <Button onClick={saveCallback} disabled={busy}
                    className="rounded-md h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="dbh-schedule-save">
              {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Check size={13} className="mr-1.5" />}
              Schedule callback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CounterPill({ icon, value, testid }) {
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-white border border-slate-200"
         data-testid={testid}>
      {icon}
      <span className="text-[11px] font-semibold text-slate-700 tabular-nums">{value ?? 0}</span>
    </div>
  );
}

function LabeledField({ label, value, testid, onCopy }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        {label}
      </div>
      <div className="text-[11px] text-slate-700 truncate flex items-center gap-1" data-testid={testid}>
        <span className="truncate">
          {value || <span className="italic text-slate-300">—</span>}
        </span>
        {value && onCopy && (
          <button onClick={onCopy} className="text-slate-400 hover:text-slate-600 shrink-0"
                  data-testid={`${testid}-copy`}
                  title={`Copy ${label.toLowerCase()}`}>
            <Copy size={10} />
          </button>
        )}
      </div>
    </div>
  );
}
